import { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { globalSearch, fetchFilters } from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import { Search as SearchIcon, X } from "lucide-react";

export default function Search() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const initial = params.get("q") || "";
  const [query, setQuery] = useState(initial);
  const [results, setResults] = useState({ terms: [], products: [] });
  const [filters, setFilters] = useState({ concerns: [], ingredients: [] });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = "Rechercher – Glossaire Beckyliss";
    fetchFilters().then(setFilters).catch(() => {});
  }, []);

  useEffect(() => {
    const q = params.get("q") || "";
    setQuery(q);
    if (q.trim().length >= 1) {
      setLoading(true);
      globalSearch(q.trim())
        .then(setResults)
        .finally(() => setLoading(false));
    } else {
      setResults({ terms: [], products: [] });
    }
  }, [params]);

  const submit = (e) => {
    e.preventDefault();
    if (query.trim().length === 0) return;
    setParams({ q: query.trim() });
  };

  const clear = () => {
    setQuery("");
    setParams({});
  };

  const applyConcern = (c) => navigate(`/recherche?q=${encodeURIComponent(c)}`);

  return (
    <div className="fade-in-up" data-testid="search-page">
      <section className="border-b border-bl-border bg-bl-soft">
        <div className="max-w-editorial mx-auto px-6 md:px-10 lg:px-14 py-20 md:py-28">
          <span className="overline">Recherche</span>
          <h1 className="font-serif text-5xl md:text-6xl text-bl-text mt-3 leading-tight">
            Trouver un terme, un produit, une référence
          </h1>
          <form onSubmit={submit} className="mt-10 max-w-2xl flex items-center border-b border-bl-dore-light pb-3">
            <SearchIcon size={20} strokeWidth={1.4} className="text-bl-textSoft mr-3" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Sérum, acide hyaluronique, BL-CR-001…"
              className="flex-1 bg-transparent outline-none text-lg text-bl-text font-light placeholder:text-bl-textSoft/70"
              data-testid="search-page-input"
            />
            {query && (
              <button type="button" onClick={clear} className="text-bl-textSoft hover:text-bl-text mr-3" data-testid="search-clear">
                <X size={18} />
              </button>
            )}
            <button
              type="submit"
              className="overline text-bl-dore hover:text-bl-text"
              data-testid="search-page-submit"
            >
              Chercher
            </button>
          </form>
        </div>
      </section>

      <section className="max-w-editorial mx-auto px-6 md:px-10 lg:px-14 py-16 grid lg:grid-cols-12 gap-10" data-testid="search-results">
        {/* Filters */}
        <aside className="lg:col-span-3">
          <span className="overline">Filtres</span>
          <h2 className="font-serif text-2xl mt-2 mb-6">Préoccupations</h2>
          <div className="flex flex-wrap gap-2">
            {filters.concerns.slice(0, 16).map((c) => (
              <button
                key={c}
                onClick={() => applyConcern(c)}
                className="px-3 py-1.5 text-xs border border-bl-border rounded-full hover:bg-bl-rose hover:border-bl-roseDeep transition-colors"
                data-testid={`filter-concern-${c}`}
              >
                {c}
              </button>
            ))}
          </div>
          <h2 className="font-serif text-2xl mt-10 mb-6">Ingrédients</h2>
          <div className="flex flex-wrap gap-2">
            {filters.ingredients.slice(0, 16).map((i) => (
              <button
                key={i}
                onClick={() => applyConcern(i)}
                className="px-3 py-1.5 text-xs border border-bl-border rounded-full hover:bg-bl-rose hover:border-bl-roseDeep transition-colors"
                data-testid={`filter-ingredient-${i}`}
              >
                {i}
              </button>
            ))}
          </div>
        </aside>

        {/* Results */}
        <div className="lg:col-span-9">
          {loading && <p className="text-bl-textSoft">Recherche en cours…</p>}

          {!loading && initial && results.terms.length === 0 && results.products.length === 0 && (
            <p className="text-bl-textSoft" data-testid="no-results">Aucun résultat pour « {initial} ».</p>
          )}

          {results.terms.length > 0 && (
            <div className="mb-16">
              <div className="flex items-baseline justify-between mb-6">
                <h3 className="font-serif text-3xl">Termes du glossaire</h3>
                <span className="overline">{results.terms.length}</span>
              </div>
              <ul className="divide-y divide-bl-border border-y border-bl-border">
                {results.terms.map((t) => (
                  <li key={t.slug}>
                    <Link
                      to={`/terme/${t.slug}`}
                      className="group block py-5 grid grid-cols-12 gap-4 items-baseline"
                      data-testid={`result-term-${t.slug}`}
                    >
                      <span className="col-span-3 overline">{t.category_slug}</span>
                      <h4 className="col-span-9 md:col-span-5 font-serif text-2xl group-hover:text-bl-dore transition-colors">
                        {t.name}
                      </h4>
                      <p className="hidden md:block col-span-4 text-xs text-bl-textSoft">{t.short_definition}</p>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {results.products.length > 0 && (
            <div>
              <div className="flex items-baseline justify-between mb-6">
                <h3 className="font-serif text-3xl">Produits Beckyliss</h3>
                <span className="overline">{results.products.length}</span>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {results.products.map((p) => (
                  <ProductCard key={p.slug} product={p} />
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
